import { Box, Button, InputAdornment, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { RiFileDownloadLine } from "react-icons/ri";
import { FiSearch } from "react-icons/fi";
import CustomTabs from "../../Components/Common/CustomTabs";
import Loader from "../../Components/Common/Loader.js";
import ProductTableWithPagination from "../../Components/Products.js/ProductTableWithPagination";
import { useQuery } from "@apollo/client";
import { GET_PRODUCTS, GET_PRODUCTS_BY_DATERANGE } from "../../Utils/Queries.js";
import CustomSelect from "../../Components/Common/CustomSelect.js";
import jsPDF from "jspdf";
import "jspdf-autotable";

const days = [
  { value: "1", label: "Today" },
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: 0, label: "All" },
];

const DEFAULT_PAGE_SIZE = 10;

/**
 * API expects GraphQL Float! for page/limit. NaN becomes `null` in JSON and triggers
 * "must not be null" — always coerce to a finite number ≥ 1.
 */
function toPaginationFloat(value, fallback) {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 1) {
    return fallback;
  }
  return n;
}

/** Normalize API pagination (object or missing). */
function normalizeProductPagination(raw, currentPage, pageSize, itemCount) {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const totalItems =
      Number(
        raw.totalItems ??
          raw.totalCount ??
          raw.count ??
          /* ProductPaginationDto */
          0,
      ) || 0;
    const inferredPages =
      totalItems > 0 && pageSize > 0
        ? Math.max(1, Math.ceil(totalItems / pageSize))
        : 1;
    const totalPages = Math.max(
      1,
      Number(raw.totalPages ?? raw.totalPage) || inferredPages,
    );
    return {
      totalItems,
      totalPages,
      currentPage:
        Number(raw.currentPage) ||
        currentPage,
      itemsPerPage: Number(raw.itemsPerPage) || pageSize,
    };
  }
  const totalItems = itemCount;
  const totalPages = Math.max(
    1,
    pageSize > 0 ? Math.ceil(totalItems / pageSize) || 1 : 1,
  );
  return {
    totalItems,
    totalPages,
    currentPage,
  };
}

const Products = () => {
  const [value, setValue] = React.useState(0);
  const [dateRange, setDateRange] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [paginationInfo, setPaginationInfo] = useState({
    totalPages: 1,
    totalItems: 0,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const pageNum = useMemo(
    () => toPaginationFloat(page, 1),
    [page],
  );
  const limitNum = useMemo(
    () => toPaginationFloat(rowsPerPage, DEFAULT_PAGE_SIZE),
    [rowsPerPage],
  );

  const textToSearch = useMemo(
    () =>
      debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : null,
    [debouncedSearch],
  );

  const allProductsQuery = useQuery(GET_PRODUCTS, {
    variables: {
      page: pageNum,
      limit: limitNum,
      textToSearch,
    },
    skip: dateRange !== 0,
    fetchPolicy: "network-only",
  });

  const dateRangeQuery = useQuery(GET_PRODUCTS_BY_DATERANGE, {
    variables: {
      dateRangeKey: String(dateRange),
      page: pageNum,
      limit: limitNum,
      textToSearch,
    },
    skip: dateRange === 0,
    fetchPolicy: "network-only",
  });

  const { loading, data } =
    dateRange === 0 ? allProductsQuery : dateRangeQuery;

  const listItems = useMemo(() => {
    const raw = data?.fetchProductsWithFilter?.items ?? [];
    switch (value) {
      case 1:
        return raw.filter((i) => i?.status === "PENDING");
      case 2:
        return raw.filter((i) => i?.status === "APPROVED");
      case 3:
        return raw.filter((i) => i?.status === "DEACTIVATED");
      default:
        return raw;
    }
  }, [data, value]);

  useEffect(() => {
    const raw = data?.fetchProductsWithFilter?.pagination;
    const itemCount = data?.fetchProductsWithFilter?.items?.length ?? 0;
    setPaginationInfo(
      normalizeProductPagination(raw, page, rowsPerPage, itemCount),
    );
  }, [data, page, rowsPerPage]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setPage(1);
  };

  const handleChangeDateRange = (next) => {
    setDateRange(next);
    setPage(1);
  };

  /** Supports MUI `(event, page)` or a single `page` arg (table calls one arg). */
  const handlePageChange = (arg1, arg2) => {
    const newPage =
      typeof arg2 === "number" && Number.isFinite(arg2) ? arg2 : arg1;
    if (
      typeof newPage === "number" &&
      Number.isFinite(newPage) &&
      newPage >= 1
    ) {
      setPage(newPage);
    }
  };

  const handleRowsPerPageChange = (n) => {
    const next = toPaginationFloat(n, DEFAULT_PAGE_SIZE);
    setRowsPerPage(next);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const tableProps = {
    data: listItems,
    loading,
    pagination: paginationInfo,
    page,
    rowsPerPage,
    onPageChange: handlePageChange,
    onRowsPerPageChange: handleRowsPerPageChange,
  };

  const TabData = [
    { label: "All Products", component: <ProductTableWithPagination {...tableProps} /> },
    { label: "Pending", component: <ProductTableWithPagination {...tableProps} /> },
    { label: "Active", component: <ProductTableWithPagination {...tableProps} /> },
    { label: "Deactivated", component: <ProductTableWithPagination {...tableProps} /> },
  ];

  const generatePDF = () => {
    const doc = new jsPDF();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);

    doc.text(`Mamaket Product List - ${formatDate(new Date())}`, 10, 10);
    doc.setFont("helvetica");
    doc.setFontSize(12);

    const tableColumn = [
      "S/N",
      "Reference",
      "Name",
      "Price",
      "Description",
      "In Stock",
      "Rating",
      "Status",
      "Date",
    ];
    const tableRows = [];

    listItems.forEach((item, index) => {
      const itemData = [
        index + 1,
        item.reference ?? "—",
        item.name ?? "—",
        item.currency?.symbol
          ? `${item.currency.symbol}${item.price}`
          : String(item.price ?? "—"),
        item.description ?? "—",
        item.isInStock ? "Yes" : "No",
        item.rating ?? "—",
        item.status ?? "—",
        item.createdAt ? formatDate(item.createdAt) : "—",
      ];
      tableRows.push(itemData);
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 15,
      styles: {
        fontSize: 8,
        font: "helvetica",
      },
      headStyles: {
        fillColor: [92, 48, 105],
      },
    });

    doc.save(`mamaket_pl(${new Date()}).pdf`);
  };

  const hasProductData = Boolean(data?.fetchProductsWithFilter);

  return (
    <>
      {loading && !hasProductData && <Loader />}
      <Box sx={{ bgcolor: "#fff", p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <TextField
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search by ID, product, or others..."
            sx={{ width: "70%" }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch style={{ fontSize: "18px" }} />
                </InputAdornment>
              ),
              style: {
                background: " #F8F7F7",
                fontSize: "13px",
                borderRadius: "5px",
                border: 0,
                offset: " 0px solid #f2f2f2",
              },
            }}
          />
          <Box sx={{ width: "13%", bgcolor: "#F8F7F7" }}>
            <CustomSelect
              label={"Date Range"}
              list={days}
              handleChangeDateRange={handleChangeDateRange}
            />
          </Box>
          <Button
            onClick={generatePDF}
            endIcon={<RiFileDownloadLine style={{ color: "#666" }} />}
            sx={{
              color: "#666",
              bgcolor: "#F8F7F7",
              width: "10%",
              py: 1,
              fontSize: "12px",
            }}
          >
            Download
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <CustomTabs
            value={value}
            handleChange={handleChange}
            data={TabData}
            variant="standard"
          />
        </Box>
      </Box>
    </>
  );
};

export default Products;

const formatDate = (dateString) => {
  const date = new Date(dateString);

  const day = date.getUTCDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
};
